import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Routes, Route, useNavigate, useParams } from 'react-router-dom'
import { Container, Row, Col, Alert } from 'react-bootstrap'
import { generateReceipt, salesList } from '../actions/posActions.js'
import { checkoutSale } from '../actions/inventoryActions.js'
import SearchPos from '../components/PosSearchBox.js'
import PaginatePos from '../components/PaginatePos.js'
import Message from '../components/Message.js'
import Loader from '../components/Loader.js'
import PosProductTable from '../components/pos/PosProductTable.js'
import PosReceiptCard from '../components/pos/PosReceiptCard.js'
import { ThemeContext } from '../components/ThemeContext.js'
import { MULTI_INVENTORY_UPDATE_RESET } from '../constants/inventoryConstants.js'
import { GENERATE_RECEIPT_RESET } from '../constants/posConstants.js'
import { GET_SALE_ITEM_RESET } from '../constants/saleConstants.js'
import { trackEvent, toAnalyticsItem } from '../utils/analytics.js'
import {
  defaultItemInput,
  clampQuantity,
  clampDiscount,
  buildCartItem,
  computeTotals,
  buildReceiptPayload,
  buildReceiptFilename
} from '../utils/cart.js'

const ANALYTICS_CURRENCY = 'USD'

const Pointofsale = () => {
  const { theme } = useContext(ThemeContext)
  const [show, setShow] = useState(false)
  const [shipping, setShipping] = useState(0)
  const [qrid, setQrid] = useState('')
  const [cartItems, setCartItems] = useState([])
  const [itemInputs, setItemInputs] = useState({})
  const [notice, setNotice] = useState(null)
  const [pendingPurchase, setPendingPurchase] = useState(null)

  const dispatch = useDispatch()
  const navigate = useNavigate()

  const handleClose = () => setShow(false)

  const { pageNumberpos = 1, keyword = '' } = useParams()

  const userLogin = useSelector((state) => state.userLogin)
  const { userInfo } = userLogin

  const ownerLogin = useSelector((state) => state.ownerLogin)
  const { ownerInfo } = ownerLogin

  const activeSession = ownerInfo || userInfo
  const bname = activeSession?.bname || activeSession?.name || ''
  const vfname = bname.toLowerCase().replace(/\s/g, '')

  const saleList = useSelector((state) => state.saleList)
  const { sales = [], loading, error, pagespos, pagepos, countpos } = saleList

  const settingList = useSelector((state) => state.settingList)
  const { settings = {} } = settingList

  const multiInventoryUpdate = useSelector(
    (state) => state.multiInventoryUpdate || {}
  )
  const {
    success: inventoryUpdateSuccess,
    error: inventoryUpdateError,
    sale: completedSale
  } = multiInventoryUpdate

  const { error: receiptError } = useSelector(
    (state) => state.generateReceipt || {}
  )

  const taxrate = Number(settings?.taxrate || 0)

  const totals = useMemo(
    () => computeTotals(cartItems, shipping, taxrate),
    [cartItems, shipping, taxrate]
  )

  const checkoutCart = useMemo(
    () => ({
      shipping,
      qrid: qrid.trim(),
      items: cartItems.map((item) => ({
        id: item.id,
        quantity: item.quantity,
        discount: item.itemdiscount
      }))
    }),
    [cartItems, shipping, qrid]
  )

  const handleShow = () => {
    if (cartItems.length > 0) {
      trackEvent('begin_checkout', {
        currency: ANALYTICS_CURRENCY,
        value: totals.total,
        items: cartItems.map(toAnalyticsItem)
      })
    }

    setShow(true)
  }

  const childToparent = (data) => {
    const nextShipping = Math.max(0, Number(data) || 0)
    setShipping(nextShipping)
  }

  const handleItemInputChange = (id, field, value, maxQty = Infinity) => {
    setItemInputs((prev) => ({
      ...prev,
      [id]: {
        ...(prev[id] || defaultItemInput),
        [field]:
          field === 'quantity' ?
            clampQuantity(value, maxQty)
          : clampDiscount(value)
      }
    }))
  }

  const deleteItem = (id) => {
    const removedItem = cartItems.find((item) => item.id === id)

    if (removedItem) {
      trackEvent('remove_from_cart', {
        currency: ANALYTICS_CURRENCY,
        value: removedItem.lineSubtotal,
        items: [toAnalyticsItem(removedItem)]
      })
    }

    setCartItems((prev) => prev.filter((item) => item.id !== id))
    setNotice(null)
  }

  const addToReceipt = (unit) => {
    if (cartItems.some((item) => item.id === unit._id)) {
      setNotice({
        variant: 'warning',
        message: 'Item already in receipt.'
      })
      return
    }

    const cartItem = buildCartItem(unit, itemInputs[unit._id])

    setCartItems((prev) => [...prev, cartItem])

    trackEvent('add_to_cart', {
      currency: ANALYTICS_CURRENCY,
      value: cartItem.lineSubtotal,
      items: [toAnalyticsItem(cartItem)]
    })

    setItemInputs((prev) => ({
      ...prev,
      [unit._id]: defaultItemInput
    }))

    setNotice(null)
  }

  const resetReceiptState = () => {
    setCartItems([])
    setItemInputs({})
    setShipping(0)
    setQrid('')
    setNotice(null)
  }

  const cancelRcpt = () => {
    if (cartItems.length === 0) {
      return
    }

    resetReceiptState()
    handleClose()
    navigate('/pos')
  }

  const checkOut = (e) => {
    e.preventDefault()

    if (cartItems.length === 0) {
      setNotice({
        variant: 'warning',
        message: 'Add at least one item before checkout.'
      })
      handleClose()
      return
    }

    setPendingPurchase({
      qrid: qrid.trim(),
      affiliation: bname || undefined,
      items: cartItems.map(toAnalyticsItem)
    })

    dispatch(checkoutSale(checkoutCart))

    handleClose()
    resetReceiptState()
    navigate('/pos')
  }

  useEffect(() => {
    dispatch(salesList(pageNumberpos, keyword))
  }, [dispatch, pageNumberpos, keyword])

  useEffect(() => {
    if (!pendingPurchase || !inventoryUpdateSuccess || !completedSale) {
      return
    }

    trackEvent('purchase', {
      transaction_id: completedSale.barcode,
      affiliation: pendingPurchase.affiliation,
      currency: ANALYTICS_CURRENCY,
      value: completedSale.total,
      tax: completedSale.taxes,
      shipping: completedSale.shipping,
      items: pendingPurchase.items
    })

    if (pendingPurchase.qrid !== '') {
      dispatch(
        generateReceipt(buildReceiptPayload(completedSale), {
          qrid: pendingPurchase.qrid,
          qfname: buildReceiptFilename(completedSale.barcode, vfname),
          barcode: completedSale.barcode
        })
      )
    }

    setNotice({
      variant: 'success',
      message: `Sale recorded. Receipt ${completedSale.barcode}`
    })
    setPendingPurchase(null)
    dispatch({ type: MULTI_INVENTORY_UPDATE_RESET })
    dispatch(salesList(pageNumberpos, keyword))
  }, [
    dispatch,
    inventoryUpdateSuccess,
    completedSale,
    pendingPurchase,
    vfname,
    pageNumberpos,
    keyword
  ])

  useEffect(() => {
    if (!receiptError) {
      return
    }

    setNotice({
      variant: 'warning',
      message: `Sale recorded, but the receipt could not be sent: ${receiptError}`
    })
    dispatch({ type: GENERATE_RECEIPT_RESET })
  }, [dispatch, receiptError])

  useEffect(() => {
    if (!pendingPurchase || !inventoryUpdateError) {
      return
    }

    setNotice({
      variant: 'danger',
      message: `Sale was not recorded: ${inventoryUpdateError}`
    })
    setPendingPurchase(null)
    dispatch({ type: MULTI_INVENTORY_UPDATE_RESET })
    dispatch(salesList(pageNumberpos, keyword))
  }, [dispatch, inventoryUpdateError, pendingPurchase, pageNumberpos, keyword])

  useEffect(() => {
    if (!activeSession) {
      dispatch({ type: GET_SALE_ITEM_RESET })
      navigate('/login')
    }
  }, [dispatch, navigate, activeSession])

  return (
    <Container
      className='pos-terminal'
      style={{
        color: theme.text,
        '--pos-border': theme.surfaceMuted,
        '--pos-surface': theme.card,
        '--pos-text': theme.text,
        '--pos-muted': theme.subtext,
        '--pos-accent': theme.accent,
        '--pos-soft-accent': theme.surfaceMuted
      }}
    >
      <Row className='align-items-end my-3 pos-heading'>
        <Col>
          <h1 className='mb-1' style={{ color: theme.text }}>
            Sales terminal
          </h1>
          <p className='mb-0' style={{ color: theme.subtext }}>
            Find products, add them to the sale, and review the total.
          </p>
        </Col>
      </Row>
      <Row className='align-items-center my-3 pos-search-row'>
        <Col>
          <Routes>
            <Route path='/*' element={<SearchPos />} />
          </Routes>
        </Col>
      </Row>

      <div className='d-flex align-items-center justify-content-between flex-wrap gap-2 pos-results-toolbar'>
        <h2 className='pos-section-title mb-0'>Products</h2>
        <span className='pos-results-count' style={{ color: theme.subtext }}>
          {countpos ?? 0} products
        </span>
      </div>
      <div className='pos-pagination'>
        <PaginatePos pagespos={pagespos} pagepos={pagepos} />
      </div>
      {notice && (
        <Alert
          variant={notice.variant}
          dismissible
          onClose={() => setNotice(null)}
        >
          {notice.message}
        </Alert>
      )}
      {countpos === 0 && <Message variant='info'>None Found</Message>}
      {loading ?
        <Loader />
      : error ?
        <Message variant='danger'>{error}</Message>
      : <Row className='g-3 align-items-start pos-workspace'>
          <Col
            xs={12}
            lg={7}
            className='pos-catalog-panel'
            style={{ minWidth: 0 }}
          >
            <PosProductTable
              sales={sales}
              itemInputs={itemInputs}
              onInputChange={handleItemInputChange}
              onAdd={addToReceipt}
            />
          </Col>
          <Col
            xs={12}
            lg={5}
            className='pos-cart-column'
            style={{ minWidth: 0 }}
          >
            <PosReceiptCard
              theme={theme}
              cartItems={cartItems}
              totals={totals}
              taxrate={taxrate}
              onDelete={deleteItem}
              onShippingChange={childToparent}
              onCancelSale={cancelRcpt}
              showCheckout={show}
              onOpenCheckout={handleShow}
              onCloseCheckout={handleClose}
              onSaveCheckout={checkOut}
              qrid={qrid}
              onQridChange={setQrid}
            />
          </Col>
        </Row>
      }
    </Container>
  )
}

export default Pointofsale
